import type { QuoteRevisionSnapshot } from "../../types/revision.type";
import type { RfxQuoteItem } from "../../types/quote.type";
import type { QuoteTableColumn, QuoteTableRow } from "../../../components/QuoteTable/quote-table.types";
import type { ReadOnlyRow } from "./components/ReadOnlyQuoteTable";
import type { SummaryTotalRow } from "../../../components/QuoteTable/QuoteSummaryTotals";

/**
 * Returns a Set of field keys (e.g. "q-123.quantity", "q-123.rate") that differ
 * between two revisions.
 */
export const getQuoteDiff = (
	a: QuoteRevisionSnapshot,
	b: QuoteRevisionSnapshot
): Set<string> => {
	const diffKeys = new Set<string>();

	const aAnswerMap = new Map(
		a.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);
	const bAnswerMap = new Map(
		b.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);

	const allItemIds = new Set([...aAnswerMap.keys(), ...bAnswerMap.keys()]);

	let aSubtotal = 0;
	let bSubtotal = 0;

	allItemIds.forEach((itemId) => {
		const aAns = aAnswerMap.get(itemId);
		const bAns = bAnswerMap.get(itemId);

		if (!aAns || !bAns) {
			diffKeys.add(`q-${itemId}.quantity`);
			diffKeys.add(`q-${itemId}.rate`);
			diffKeys.add(`q-${itemId}.cost`);
			if (aAns) {
				aSubtotal +=
					Number(aAns.answer.value.quantity) * Number(aAns.answer.value.rate);
			}
			if (bAns) {
				bSubtotal +=
					Number(bAns.answer.value.quantity) * Number(bAns.answer.value.rate);
			}
			return;
		}

		const aQty = Number(aAns.answer.value.quantity);
		const bQty = Number(bAns.answer.value.quantity);
		const aRate = Number(aAns.answer.value.rate);
		const bRate = Number(bAns.answer.value.rate);

		aSubtotal += aQty * aRate;
		bSubtotal += bQty * bRate;

		if (aQty !== bQty) {
			diffKeys.add(`q-${itemId}.quantity`);
		}
		if (aRate !== bRate) {
			diffKeys.add(`q-${itemId}.rate`);
		}
		if (aQty * aRate !== bQty * bRate) {
			diffKeys.add(`q-${itemId}.cost`);
		}
	});

	if (aSubtotal !== bSubtotal) {
		diffKeys.add("total.subtotal");
		diffKeys.add("total.gst");
		diffKeys.add("total.totalcost");
	}

	return diffKeys;
};

/**
 * Formats a revision number for display.
 * 0 → 'Original', N → 'Revision N'
 */
export const formatRevisionLabel = (revision: number): string => {
	if (revision === 0) {
		return "Original";
	}
	return `Revision ${revision}`;
};

/**
 * Formats a number as AUD currency.
 */
export const formatCurrency = (value: number): string =>
	value.toLocaleString("en-AU", { style: "currency", currency: "AUD" });

/**
 * Total row types used by QuoteTableEdit and ReadOnlyQuoteTable.
 */
export enum TotalType {
	GST = "gst",
	SubTotal = "subtotal",
	Total = "totalcost",
}

export const SUBTOTAL_ROW = {
	id: TotalType.SubTotal,
	label: "Subtotal",
	color: "#475467",
} as const;

export const TOTAL_ROWS = [
	SUBTOTAL_ROW,
	{ id: TotalType.GST, label: "GST (10%)", color: "#475467" },
	{ id: TotalType.Total, label: "Total Cost", color: "#101828" },
] as const;

/**
 * Calculates subtotal, GST, or total cost from an array of QuoteTableRows.
 * When rows have quantityCategories (value columns), total per row is:
 *   quantity × sum(value column rates). If no quantity, sum of rates.
 * Falls back to simple quantity × rate for rows without value columns.
 */
export const calculateTotalFromRows = (
	rows: QuoteTableRow[],
	type: TotalType
): number => {
	const subtotal = rows.reduce((acc, row) => {
		if (row.quantityCategories && Object.keys(row.quantityCategories).length > 0) {
			const valueSum = Object.values(row.quantityCategories).reduce(
				(sum, cat) => sum + Number(cat.rate || 0),
				0
			);
			return acc + (row.quantity ? row.quantity * valueSum : valueSum);
		}
		return acc + row.quantity * row.rate;
	}, 0);
	switch (type) {
		case TotalType.SubTotal:
			return subtotal;
		case TotalType.GST:
			return subtotal * 0.1;
		case TotalType.Total:
			return subtotal + subtotal * 0.1;
		default:
			return 0;
	}
};

/**
 * Triggers a browser download for a Blob.
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};

/**
 * Converts a QuoteRevisionSnapshot's answers into QuoteTableRow[].
 * When `quoteItems` is provided, item names and unit labels are resolved
 * from the quote definition rather than showing generic "Item N".
 */
export const mapSnapshotToRows = (
	snapshot: QuoteRevisionSnapshot,
	quoteItems?: RfxQuoteItem[]
): QuoteTableRow[] => {
	const itemMap = quoteItems
		? new Map(quoteItems.map((item) => [item.id, item]))
		: undefined;

	return snapshot.answers
		.map((ans) => {
			const item = itemMap?.get(ans.rfq_quote_item_id);
			return {
				itemId: ans.rfq_quote_item_id,
				name: item?.name ?? `Item ${ans.rfq_quote_item_id}`,
				unitLabel: item?.unitMeasurement,
				quantity: Number(ans.answer.value.quantity),
				rate: Number(ans.answer.value.rate),
				sort_order: Number(item?.sortOrder),
			};
		})
		.sort((a, b) => a.sort_order - b.sort_order);
};

/**
 * Returns a Set of diff keys for cell-based revisions.
 * Keys are in the format "cell-{rowId}-{columnId}".
 */
export const getCellDiff = (
	currentCells: Record<number, Record<number, string>>,
	previousCells: Record<number, Record<number, string>>
): Set<string> => {
	const diffKeys = new Set<string>();

	const allRowIds = new Set([
		...Object.keys(currentCells).map(Number),
		...Object.keys(previousCells).map(Number),
	]);

	allRowIds.forEach((rowId) => {
		const currentRow = currentCells[rowId] ?? {};
		const previousRow = previousCells[rowId] ?? {};

		const allColIds = new Set([
			...Object.keys(currentRow).map(Number),
			...Object.keys(previousRow).map(Number),
		]);

		allColIds.forEach((colId) => {
			const currentVal = currentRow[colId] ?? '';
			const previousVal = previousRow[colId] ?? '';
			if (currentVal !== previousVal) {
				diffKeys.add(`cell-${rowId}-${colId}`);
			}
		});
	});

	return diffKeys;
};

/**
 * Converts cell edit values into QuoteTableRow[] for rendering.
 * Uses quote row definitions for names, sorts, etc.
 */
export const mapCellSnapshotToRows = (
	cellValues: Record<number, Record<number, string>>,
	quoteItems?: RfxQuoteItem[]
): QuoteTableRow[] => {
	const itemMap = quoteItems
		? new Map(quoteItems.map((item) => [item.id, item]))
		: undefined;

	if (!quoteItems) {
		return [];
	}

	return quoteItems
		.map((item) => {
			const rowCells = cellValues[item.id] ?? {};
			return {
				itemId: item.id,
				name: item.name,
				unitLabel: item.unitMeasurement,
				quantity: 0,
				rate: 0,
				columnValues: Object.fromEntries(
					Object.entries(rowCells).map(([colId, val]) => [Number(colId), val])
				),
				sort_order: Number(item.sortOrder),
			};
		})
		.sort((a, b) => a.sort_order - b.sort_order);
};

/**
 * Resolves a single cell's display value from Formik values.
 */
const resolveCellValue = (
	col: QuoteTableColumn,
	row: RfxQuoteItem,
	formikRow: Record<string, number | string> | undefined,
	allColumns: QuoteTableColumn[],
	unitLookup?: Map<number, string>
): string => {
	switch (col.columnType) {
		case "description":
			return row.name || "-";

		case "unit_of_measurement": {
			const unitVal = formikRow?.unit;
			if (unitVal != null && unitLookup) {
				return unitLookup.get(Number(unitVal)) ?? "-";
			}
			return row.unitMeasurement ?? "-";
		}

		case "response_quantity":
			return String(formikRow?.quantity ?? 0);

		case "response_value":
			return formatCurrency(Number(formikRow?.[`rate_${col.id}`] ?? 0));

		case "response_number":
			return String(formikRow?.[`number_${col.id}`] ?? 0);

		case "response_text":
			return String(formikRow?.[`text_${col.id}`] ?? "");

		case "total": {
			const quantity = Number(formikRow?.quantity ?? 0);
			const valueCols = allColumns.filter(
				(c) => c.columnType === "response_value"
			);
			const rateSum = valueCols.reduce(
				(sum, vc) => sum + Number(formikRow?.[`rate_${vc.id}`] ?? 0),
				0
			);
			const total = valueCols.length > 0 ? quantity * rateSum : quantity * Number(formikRow?.rate ?? 0);
			return formatCurrency(total);
		}

		default:
			return "-";
	}
};

/**
 * Transforms quote rows + Formik values into ReadOnlyRow[] for ReadOnlyQuoteTable.
 */
export const buildReadOnlyRows = (
	rows: RfxQuoteItem[],
	columns: QuoteTableColumn[],
	allValues: Record<string, Record<string, number | string>>,
	unitLookup?: Map<number, string>,
	isSupplier = false
): ReadOnlyRow[] => {
	const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

	return rows.map((row) => {
		const key = `quote-${row.id}`;
		const formikRow = allValues?.[key];

		return {
			rowId: row.id,
			isSupplierRow: isSupplier || row.createdByType === "supplier",
			cells: sorted.map((col) => ({
				columnId: col.id,
				displayValue: resolveCellValue(col, row, formikRow, columns, unitLookup),
			})),
		};
	});
};

/**
 * Builds pre-formatted SummaryTotalRow[] from TOTAL_ROWS and a subtotal value.
 */
export const buildReadOnlyTotals = (subtotal: number): SummaryTotalRow[] =>
	TOTAL_ROWS.map((row) => {
		let value: number;
		switch (row.id) {
			case TotalType.SubTotal:
				value = subtotal;
				break;
			case TotalType.GST:
				value = subtotal * 0.1;
				break;
			case TotalType.Total:
				value = subtotal + subtotal * 0.1;
				break;
			default:
				value = 0;
		}
		return {
			id: row.id,
			label: row.label,
			color: row.color,
			displayValue: formatCurrency(value),
		};
	});

/**
 * Computes per-column totals for response_value columns from Formik values.
 * Used by the ReadOnlyQuoteTable disabled branch in QuoteTab.
 */
export const calculateReadOnlyColumnTotals = (
	columns: QuoteTableColumn[],
	items: RfxQuoteItem[],
	allValues: Record<string, Record<string, number | string>>,
	supplierColumnTotals?: Record<number, number>
): Record<number, number> => {
	const totalsMap: Record<number, number> = {};
	const responseValueCols = columns.filter((c) => c.columnType === "response_value");

	for (const col of responseValueCols) {
		let sum = 0;
		for (const item of items) {
			const formikVal = allValues[`quote-${item.id}`]?.[`rate_${col.id}`];
			const val = Number(formikVal ?? 0);
			if (!isNaN(val)) {
				sum += val;
			}
		}
		totalsMap[col.id] = sum + (supplierColumnTotals?.[col.id] ?? 0);
	}

	return totalsMap;
};

/** Fallback columns for legacy quotes without procurer-defined columns. */
export const LEGACY_COLUMNS: QuoteTableColumn[] = [
	{ id: -1, columnType: 'description', label: 'Description of Scope', sortOrder: 1 },
	{ id: -2, columnType: 'unit_of_measurement', label: 'Unit of Measurement', sortOrder: 2 },
	{ id: -3, columnType: 'response_quantity', label: 'Quantity', sortOrder: 3 },
	{ id: -4, columnType: 'response_value', label: 'Rate', sortOrder: 4 },
	{ id: -5, columnType: 'total', label: 'Cost', sortOrder: 5 },
];

/**
 * Converts QuoteTableRow[] (from mapSnapshotToRows) to ReadOnlyRow[] for ReadOnlyQuoteTable.
 */
export const mapQuoteTableRowsToReadOnly = (
	rows: QuoteTableRow[],
	columns: QuoteTableColumn[]
): ReadOnlyRow[] => {
	return rows.map((row) => ({
		rowId: row.itemId,
		cells: columns.map((col) => {
			let displayValue: string;

			// Cell-based path: positive column IDs from DB
			if (col.id > 0 && row.columnValues) {
				displayValue = row.columnValues[col.id] ?? '-';
			} else {
				// Legacy path: map column type to row fields
				switch (col.columnType) {
					case 'description':
						displayValue = row.name || '-';
						break;
					case 'unit_of_measurement':
						displayValue = row.unitLabel ?? '-';
						break;
					case 'response_quantity':
						displayValue = String(row.quantity || 0);
						break;
					case 'response_value':
						displayValue = formatCurrency(row.rate);
						break;
					case 'total':
						displayValue = formatCurrency(row.quantity * row.rate);
						break;
					default:
						displayValue = '-';
				}
			}

			return { columnId: col.id, displayValue };
		}),
		isSupplierRow: row.createdByType === 'supplier',
	}));
};

/**
 * Translates legacy diff keys (q-{id}.quantity, q-{id}.rate, q-{id}.cost) to
 * cell format (cell-{id}-{colId}) for ReadOnlyQuoteTable.
 * Total diff keys (total.*) pass through unchanged.
 */
export const mapLegacyDiffKeysToCell = (
	legacyKeys: Set<string>,
	columns: QuoteTableColumn[]
): Set<string> => {
	const fieldToColumnType: Record<string, QuoteTableColumn['columnType']> = {
		quantity: 'response_quantity',
		rate: 'response_value',
		cost: 'total',
	};

	const columnTypeToId = new Map(columns.map((c) => [c.columnType, c.id]));
	const result = new Set<string>();

	legacyKeys.forEach((key) => {
		// Pass through total.* keys unchanged
		if (key.startsWith('total.')) {
			result.add(key);
			return;
		}

		// Parse q-{itemId}.{field}
		const match = key.match(/^q-(\d+)\.(\w+)$/);
		if (!match) {
			return;
		}

		const itemId = match[1];
		const field = match[2];
		const colType = fieldToColumnType[field];
		if (!colType) {
			return;
		}

		const colId = columnTypeToId.get(colType);
		if (colId !== undefined) {
			result.add(`cell-${itemId}-${colId}`);
		}
	});

	return result;
};
